import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../utils/useFetch";
import { loginUser } from "../../../redux/slices/userSlice";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useCountdown } from "usehooks-ts";
import OtpInput from "react-otp-input";

enum SIGNIN_STEP {
  STEP_ONE,
  STEP_TWO,
}

const SignIn = () => {
  const [phone, setPhone] = useState<string>("");
  const signInFormRef = useRef<HTMLFormElement>(null);
  const isFormDataValidated = phone.length === 11;
  const [isLoading, setIsLoading] = useState(false);
  const [signInStep, setSignInStep] = useState(SIGNIN_STEP.STEP_ONE);
  const [verifyCode, setVerifyCode] = useState("");
  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: 60,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getVerifyCodeFetch = async () => {
    const _formData = new FormData();
    _formData.append("phone", phone);
    const response = await fetch(API_BASE_URL + "v2/code", {
      method: "POST",
      body: _formData,
    });
    setIsLoading(true);
    return response.json();
  };

  const getTokenFetch = useCallback(async () => {
    const _formData = new FormData();
    _formData.append("phone", phone);
    _formData.append("code", verifyCode);
    _formData.append("version", "3.1");
    const response = await fetch(API_BASE_URL + "v2/token", {
      method: "POST",
      body: _formData,
    });
    setIsLoading(true);
    return response.json();
  }, [phone, verifyCode]);

  const handleFormStepOne = () => {
    setIsLoading(true);
    getVerifyCodeFetch().then((response) => {
      setIsLoading(false);
      if (response) {
        setSignInStep(SIGNIN_STEP.STEP_TWO);
        startCountdown();
      } else if (response === false) {
        toast.error("خطا! کد اشتراک یا شماره تلفن اشتباه است");
      }
    });
  };

  const handleFormStepTwo = useCallback(() => {
    setIsLoading(true);
    getTokenFetch().then((response) => {
      setIsLoading(false);
      if (response.state === "ok") {
        dispatch(
          loginUser({
            isLogin: true,
            token: response.token,
            userInfo: response.user_info,
          })
        );
        navigate("/");
      } else if (response.state === "incorrect" || response.state === "wrong") {
        toast.error("کد اشتباه است");
      } else {
        toast.error("کد منقضی شده است");
      }
    });
  }, [dispatch, getTokenFetch, navigate]);

  const validatePhoneNUmber = () => {
    if (/(\0)?9\d{9}/.test(phone) && phone.length === 11) {
      return true;
    }
    return false;
  };

  const sendVerifyCodeAgain = () => {
    if (count === 0) {
      getVerifyCodeFetch().then((response) => {
        setIsLoading(false);
        if (response) {
          resetCountdown();
          startCountdown();
        } else if (response === false) {
          toast.error("خطا! کد اشتراک یا شماره تلفن اشتباه است");
        }
      });
    }
  };

  useEffect(() => {
    if (verifyCode.length === 5) {
      handleFormStepTwo();
    }
  }, [verifyCode, handleFormStepTwo]);

  return (
    <>
      <div className=' w-full mx-auto p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] md:h-full'>
        <div className=' w-full h-full max-w-md md:h-auto mx-auto'>
          <div className=' bg-white rounded shadow'>
            <div className='px-6 py-6 lg:px-8'>
              <div className='mb-8 text-4xl font-bold text-center'>ورود</div>
              <form
                className='space-y-6'
                action='#'
                ref={signInFormRef}
                onSubmit={(e) => {
                  e.preventDefault();
                  signInStep === SIGNIN_STEP.STEP_ONE
                    ? handleFormStepOne()
                    : handleFormStepTwo();
                }}
              >
                <div>
                  <label
                    htmlFor='phone'
                    className='block mb-2 text-sm font-medium'
                  >
                    شماره تلفن
                  </label>
                  <input
                    type='number'
                    name='phone'
                    minLength={11}
                    maxLength={11}
                    pattern='[0-9]+'
                    disabled={signInStep === SIGNIN_STEP.STEP_TWO}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`bg-gray-50 border border-gray-300 text-center text-sm rounded ${
                      validatePhoneNUmber()
                        ? "outline-green-700"
                        : "outline-rose-700"
                    }  block w-full p-2`}
                    required
                  />
                </div>

                {signInStep === SIGNIN_STEP.STEP_TWO && (
                  <div>
                    <label
                      htmlFor='verifyCode'
                      className='block mb-2 text-sm font-medium flex'
                    >
                      <div className='flex-1'>کد فعال سازی</div>

                      <div
                        onClick={() => sendVerifyCodeAgain()}
                        className={`${
                          count === 0 ? "text-rose-900" : "text-gray-300"
                        }`}
                      >
                        ارسال مجدد {count > 0 && <>({count})</>}
                      </div>
                    </label>

                    <div style={{ direction: "ltr" }}>
                      <OtpInput
                        value={verifyCode}
                        onChange={setVerifyCode}
                        numInputs={5}
                        inputType={"number"}
                        containerStyle={{ gap: "8px" }}
                        inputStyle={{
                          border: "2px solid #ccc",
                          flex: "1",
                          borderRadius: "4px",
                          padding: "8px",
                        }}
                        shouldAutoFocus={true}
                        renderInput={(props) => <input {...props} />}
                      />
                    </div>
                  </div>
                )}

                <div
                  className={`inline-block font-normal whitespace-nowrap rounded p-1 text-center align-baseline leading-none text-rose-700 bg-rose-100`}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span className='mr-1'>
                    لطفا از اعداد انگلیسی استفاده کنید
                  </span>
                </div>
                <div className='flex items-center'>
                  <button
                    type='submit'
                    disabled={!isFormDataValidated && !isLoading}
                    className='flex-1 w-full cursor-pointer text-white bg-rose-900 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800'
                  >
                    {isLoading ? (
                      <FontAwesomeIcon
                        icon={faCircleNotch}
                        className='rotate-animation'
                      />
                    ) : (
                      <>
                        {signInStep === SIGNIN_STEP.STEP_ONE
                          ? "دریافت کد"
                          : "ورود"}
                      </>
                    )}
                  </button>
                  {signInStep === SIGNIN_STEP.STEP_ONE && (
                    <Link
                      to='/signup'
                      className='hover:underline p-2 rounded border border-rose-900 text-rose-900 mr-2'
                    >
                      ثبت نام
                    </Link>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
